import { AsyncPipe, CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { LatLng } from 'leaflet';
import { DictionaryType } from 'src/app/apiAndObjects/api/dictionaryType.enum';
import { AddObservationParams } from 'src/app/apiAndObjects/api/observations/addObservation';
import { DictionaryItemStatus } from 'src/app/apiAndObjects/objects/dictionaries/dictionaryItemStatus.enum';
import { ObservationTypesDictionaryItem } from 'src/app/apiAndObjects/objects/dictionaries/observationTypesDictionaryItem';
import { DictionaryService } from 'src/app/services/dictionary.service';
import { ObservationsService } from 'src/app/services/observations.service';
import { Dictionary } from '../../apiAndObjects/public_api';
import { DialogService } from '../dialog/dialog.service';
import { EVMapService } from '../ev-map/ev-map.service';
import { EVSidebarService } from '../ev-sidebar/ev-sidebar.service';

@Component({
  selector: 'app-ev-contribute',
  templateUrl: './ev-contribute.component.html',
  styleUrls: ['./ev-contribute.component.scss'],
  standalone: true,
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    OwlDateTimeModule,
    MatButtonModule,
    AsyncPipe,
    CommonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class EvContributeComponent {
  public form: UntypedFormGroup;
  public maxDate: Date;
  public placingPin = false;
  public resetDisabled = true;
  public searchDisabled = true;
  public responseObject = 'loading';
  public imagesControl: UntypedFormControl;
  private readonly latControl: UntypedFormControl;
  private readonly lngControl: UntypedFormControl;

  public currentObservationTypesDIs: Array<ObservationTypesDictionaryItem>;
  public keywordsDictionary: Dictionary;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly sidebarService: EVSidebarService,
    private readonly mapService: EVMapService,
    private readonly observationsService: ObservationsService,
    private readonly dictionaryService: DictionaryService,
    private readonly dialogService: DialogService,
  ) {
    this.dictionaryService
      .getDictionaries([DictionaryType.OBSERVATION_TYPES, DictionaryType.KEYWORDS])
      .then((dicts: Array<Dictionary>) => {
        this.currentObservationTypesDIs = dicts
          .shift()
          .getItems<ObservationTypesDictionaryItem>()
          .filter((item) => item.status === DictionaryItemStatus.CURRENT);
        this.keywordsDictionary = dicts.shift();
      });

    this.mapService.watchPlacingPin().subscribe((placingPin: boolean) => {
      this.placingPin = placingPin;
    });
    this.mapService.watchPlacedPinPosition().subscribe((latLng: L.LatLng) => {
      if (null != this.form) {
        this.latControl.setValue(null == latLng ? null : latLng.lat);
        this.lngControl.setValue(null == latLng ? null : latLng.lng);
      }
    });

    const currentDate = new Date();
    this.maxDate = new Date(currentDate);
    console.log(this.maxDate);

    this.form = this.fb.group({
      epsg_code: ['4326', [Validators.required]],
      date_observed: [null, [Validators.required]],
      x: [null, [Validators.required]],
      y: [null, [Validators.required]],
      observation_type_code: [null, []],
      images: [[], []],
      keywords: [[], []],
      description: [null, []],
      i_agree: [null, [Validators.requiredTrue]],
    });
    this.form.statusChanges.subscribe(() => {
      // console.log(status);
      this.searchDisabled = !this.form.valid;
    });
    this.form.valueChanges.subscribe(() => {
      // console.log('form value:', val);
      this.resetDisabled = !this.form.dirty;
    });
    this.imagesControl = this.form.get('images') as UntypedFormControl;

    this.latControl = this.form.get('y') as UntypedFormControl;
    this.latControl.valueChanges.subscribe(() => {
      // console.log(value);
      this.manualLatLngChange();
    });
    this.lngControl = this.form.get('x') as UntypedFormControl;
    this.lngControl.valueChanges.subscribe(() => {
      // console.log(value);
      this.manualLatLngChange();
    });
  }

  public closeSidePanel() {
    this.sidebarService.clearSidebarMode();
  }

  private manualLatLngChange(): void {
    const lat = this.latControl.value;
    const lng = this.lngControl.value;
    let loc: LatLng = null;
    if (null != lat && null != lng) {
      loc = new LatLng(lat, lng);
    }
    this.mapService.setPlacedPinPosition(loc);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public placePinToggle(event: MouseEvent): void {
    this.placingPin = !this.placingPin;
    this.mapService.setPlacingPin(this.placingPin);
  }

  reset() {
    this.form.reset({
      epsg_code: '4326',
      date_observed: null,
      x: null,
      y: null,
      observation_type_code: null,
      images: [],
      keywords: '',
      description: null,
      i_agree: null,
    });
  }

  uploadFile(fileList: FileList) {
    if (fileList.length > 0) {
      // TODO: test file types for images, as drag and drop disn't do this (unlike input element)

      let newArray: Array<File> = [];
      if (this.imagesControl.value) {
        newArray = this.imagesControl.value.slice();
      }
      for (let i = 0; i < fileList.length; i++) {
        newArray.push(fileList.item(i));
      }
      this.imagesControl.setValue(newArray);
    }
  }

  deleteAttachment(index: number) {
    const newArray: Array<File> = this.imagesControl.value.slice();
    newArray.splice(index, 1);
    this.imagesControl.setValue(newArray);
  }

  public responseDialog(response: Promise<void>): void {
    this.dialogService.openContributeResponse(response);
  }

  public submit(): void {
    this.form.disable();

    const response = this.observationsService
      .add({
        epsg_code: this.form.get('epsg_code').value,
        date_observed: this.form.get('date_observed').value as moment.Moment,
        x: this.form.get('x').value,
        y: this.form.get('y').value,
        observation_type_code: this.form.get('observation_type_code').value,
        images: this.form.get('images').value,
        keywords: this.form.get('keywords').value,
        description: this.form.get('description').value,
        i_agree: this.form.get('i_agree').value,
      } as AddObservationParams)
      .then(() => {
        this.reset();
      })
      .finally(() => {
        this.form.enable();
      });
    this.responseDialog(response);
  }
}
